import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useInView, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import HeroScene from "./HeroScene";

const SLIDES = [
  { id: "riviera-polo", name: "Riviera Knit Polo", line: "ATW Campaign", image: "/brand/looks/riviera-polo-v4.jpg", pos: "50% 20%" },
  { id: "md-addiction", name: "MD Addiction", line: "Eau de Parfum · House Icon", image: "/brand/md-addiction.jpg", pos: "50% 50%" },
  { id: "signature-pique", name: "Monogram Piqué Polo", line: "ATW Campaign", image: "/brand/looks/signature-pique-v4.jpg", pos: "50% 20%" },
  { id: "dewan-royal-oud", name: "Dewan Royal Oud", line: "Extrait de Parfum", image: "/brand/dewan-royal-oud.jpg", pos: "50% 50%" },
  { id: "sovereign-chronograph", name: "Sovereign Chronograph", line: "ATW Campaign", image: "/brand/looks/sovereign-chronograph-v4.jpg", pos: "50% 25%" },
  { id: "heritage-shirt", name: "Heritage Shirt", line: "ATW Campaign", image: "/brand/looks/heritage-shirt-v4.jpg", pos: "50% 20%" },
];

const lineVariants = {
  hidden: { y: "110%", rotateX: 30 },
  show: (i) => ({ y: 0, rotateX: 0, transition: { delay: 0.3 + i * 0.14, duration: 1.1, ease: [0.22, 1, 0.36, 1] } }),
};
const fade = (delay) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.9, ease: "easeOut" } });

export default function HeroCinematic() {
  const ref = useRef(null);
  const [i, setI] = useState(0);
  const inView = useInView(ref, { margin: "0px 0px -20% 0px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [22, -22]), { stiffness: 40, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 40, damping: 20 });

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % SLIDES.length), 5200);
    return () => clearInterval(t);
  }, []);

  const s = SLIDES[i];

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={(e) => { mx.set(e.clientX / window.innerWidth - 0.5); my.set(e.clientY / window.innerHeight - 0.5); }}
      className="relative min-h-[100svh] overflow-hidden bg-obsidian text-ivory"
    >
      <motion.div style={{ scale: bgScale, x: px, y: py }} className="absolute -inset-8">
        <AnimatePresence initial={false}>
          <motion.img
            key={s.id}
            data-testid="hero-slide-image"
            src={s.image}
            alt={s.name}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 7, ease: "linear" } }}
            style={{ objectPosition: s.pos }}
            className="absolute inset-0 h-full w-full object-cover opacity-90"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_72%_45%,rgba(212,175,55,0.16),transparent_55%)] pointer-events-none" />

      <div data-testid="hero-3d-scene" className="absolute inset-y-0 right-0 w-full lg:w-[58%] hidden md:block">
        <HeroScene active={inView} />
      </div>

      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-14 pt-32 lg:pt-36 pb-28 min-h-[100svh] flex items-center">
        <motion.div style={{ y: textY, opacity }} className="lg:w-[52%]">
          <motion.p {...fade(0.1)} data-testid="hero-eyebrow" className="text-[10px] md:text-xs tracking-[0.5em] uppercase text-ivory/55">
            <Link to="/about" data-testid="hero-founder-link" className="hover:text-gold-light transition-colors">Founded by Dewan Ali</Link>
            <span className="mx-3 text-gold/60">·</span>
            <span className="gold-text">A Creation of Around The World Ltd</span>
          </motion.p>

          <h1 style={{ perspective: 900 }} className="mt-8 font-serif uppercase leading-[0.86] tracking-tight text-[15vw] md:text-[10.5vw] lg:text-[6.6vw]">
            {["Around", "The"].map((line, k) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span custom={k} variants={lineVariants} initial="hidden" animate="show" className="block origin-bottom">
                  {line}
                  {k === 1 && <span className="italic font-light gold-text"> World</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div {...fade(0.8)} className="mt-8 flex items-center gap-5">
            <span className="h-px w-14 bg-gradient-to-r from-gold-dark via-gold-light to-gold-dark" />
            <p className="max-w-md text-sm md:text-base text-ivory/60 font-light leading-relaxed">
              House perfumes, wrist watches, tailoring, belts and candles — designed under one name, delivered around the world.
            </p>
          </motion.div>

          <motion.div {...fade(1)} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/shop" data-testid="hero-shop-button" className="gold-button px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-medium">
              <span className="relative z-10">Explore the Collection</span>
            </Link>
            <Link to="/lookbook" data-testid="hero-bestsellers-button" className="group flex items-center gap-2 px-2 py-4 text-[11px] tracking-[0.25em] uppercase text-ivory/70 hover:text-gold-light transition-colors">
              ATW Campaign
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.div {...fade(1.2)} className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            {[["07", "Collections"], ["05", "House perfumes"], ["190+", "Countries served"]].map(([n, l]) => (
              <div key={l} className="border-l border-gold/25 pl-4">
                <p className="font-serif text-3xl gold-text">{n}</p>
                <p className="text-[9px] tracking-[0.3em] uppercase mt-1 text-ivory/45">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div {...fade(1.4)} className="absolute bottom-8 right-6 lg:right-14 z-10 flex items-center gap-6">
        <div className="flex gap-2">
          {SLIDES.map((x, k) => (
            <button key={x.id} data-testid={`hero-slide-dot-${k}`} onClick={() => setI(k)} aria-label={x.name} className={`h-px transition-[width,background-color] duration-500 ${k === i ? "w-10 bg-gold-light" : "w-4 bg-ivory/30"}`} />
          ))}
        </div>
        <Link to={`/product/${s.id}`} data-testid="hero-featured-link" className="glass-dark px-5 py-3.5 flex items-center gap-4 hover:border-gold/60 transition-colors min-w-[240px]">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }} className="min-w-[150px]">
              <p data-testid="hero-slide-name" className="text-[9px] tracking-[0.3em] uppercase text-gold-light/80">{s.line}</p>
              <p className="font-serif text-lg mt-0.5">{s.name}</p>
            </motion.div>
          </AnimatePresence>
          <ArrowUpRight size={16} className="ml-auto text-gold-light" />
        </Link>
      </motion.div>

      <motion.div {...fade(1.6)} className="absolute bottom-8 left-6 lg:left-14 z-10 flex items-center gap-3 text-ivory/45">
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}><ArrowDown size={15} strokeWidth={1.5} /></motion.span>
        <span className="text-[9px] tracking-[0.4em] uppercase">Scroll to discover</span>
      </motion.div>
    </section>
  );
}
